import { useWhiteLabelContext } from '@aibees/elephant/feature/white-label/context';
import { Icon, LockIcon } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { memo } from 'react';
import * as Styled from './styles';
import * as SX from './sx';
import type { FaviconPreviewProps as Props } from './types';

export const FaviconPreview = memo(({ className }: Props) => {
  const { palette } = useTheme();

  const { favicon, companyDomain, companyName } = useWhiteLabelContext();

  const logoUrl = favicon.value
    ? URL.createObjectURL(favicon.value as any)
    : null;

  const domain = !companyDomain.error ? companyDomain.value : undefined;
  const name = !companyName.error ? companyName.value : undefined;

  return (
    <Styled.Wrapper
      className={classnames('FaviconPreview', className)}
      title="Favicon"
      description="16px X 16px"
      tooltipContent="Recommended dimensions: 16px by 16px. This icon appears in the browser tab."
    >
      <Stack sx={SX.root}>
        <Box sx={SX.topBox} />
        <Stack sx={{ px: 0 }} direction="row">
          <Stack
            direction="row"
            gap={0.75}
            sx={SX.functionTab}
            alignItems="center"
          >
            <Box sx={SX.dot(palette.error)} />
            <Box sx={SX.dot(palette.warning)} />
            <Box sx={SX.dot(palette.success)} />
            <Box sx={SX.funcTabBox1}>
              <Box sx={SX.funcTabBox2} />
            </Box>
          </Stack>
          <Stack sx={SX.tab} direction="row" gap={1} alignItems="center">
            {logoUrl ? (
              <img src={logoUrl} alt="Logo" />
            ) : (
              <Styled.Logo src="/assets/images/ark-logo.svg" alt="logo" />
            )}

            <Typography sx={SX.companyName}>{name || 'Ai Ark'}</Typography>
            <Icon name="close" size={14} />
            <Box sx={SX.tabBox1}>
              <Box sx={SX.tabBox2} />
            </Box>
          </Stack>
          <Stack sx={SX.addSection} direction="row" alignItems="center">
            <Icon name="plus" />
          </Stack>
        </Stack>
        <Stack direction="row" alignItems="center" gap={2} sx={SX.action}>
          <Stack direction="row" gap={1}>
            <Icon name="arrow-left" size={18} sxx={SX.icon} />
            <Icon name="arrow-right" size={18} sxx={SX.icon2} />
            <Icon name="reload" size={18} sxx={SX.icon} />
            <Icon name="home-light" size={18} sxx={SX.icon} />
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={SX.searchBar}
          >
            <Stack direction="row" alignItems="center" gap={1}>
              <LockIcon sx={SX.icon} />
              <Typography>{domain || 'app.ai-ark.com'}</Typography>
            </Stack>
            <Icon name="star-outline" size={16} sxx={SX.icon} />
          </Stack>
          <Stack direction="row" alignItems="center" gap={1}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              sx={SX.avatar}
            >
              <Icon name="avatar" size={16} sxx={SX.icon} />
            </Stack>
            <Icon name="more-vertical" size={18} />
          </Stack>
        </Stack>
      </Stack>
    </Styled.Wrapper>
  );
});

FaviconPreview.displayName = 'FaviconPreview';
